import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreIcon,
  AddCircle as PlusCircle,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { Menu, MenuItem } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AuthGuard from '@/components/auth-guard';
import { FeatureGuard } from '@/components/feature-guard';
import { Page } from '@/components/page';
import { useCodexAccessLayer } from '@/features/codex';
import { useResponsiveValue } from '@/hooks/use-responsive-value';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import type { EntitySummary } from '@/lib/types';
import { EntityListSortMenu } from '@/components/entity-list-sort-menu';
import { useEntityListSort } from '@/hooks/use-entity-list-sort';

import { CategorySelectionDialog } from '@/features/codex/components/category-selection-dialog';
import { CodexGrid } from '@/features/codex/components/codex-grid';
import { NewCategoryDialog } from '@/features/codex/components/new-category-dialog';
import { RenameCategoryDialog } from '@/features/codex/components/rename-category-dialog';
import { MoveCategoryDialog } from '@/features/codex/components/move-category-dialog';

const DEFAULT_CATEGORIES = [
  'magicSystem',
  'races',
  'religions',
  'technology',
  'planets',
  'caseFiles',
  'creatures',
  'rules'
];

export const Route = createFileRoute('/codex/')({
  component: CodexPage,
});

function CodexContent() {
  const { novel, updateNovelItem, getSummaries } = useNovelRepository();
  const entries = getSummaries<EntitySummary>('codex');
  const navigate = useNavigate();
  const resolveResponsive = useResponsiveValue();
  const { isLoading } = useCodexAccessLayer();
  const { t } = useTranslation();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState('all');
  const sortKey = activeTab === 'all' ? 'codex' : ('codex:' + activeTab);
  const { sort, setSort, sortItems } = useEntityListSort(sortKey);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<{ oldName: string; newName: string; parentId?: string } | null>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const [allTabNewEntryDialogOpen, setAllTabNewEntryDialogOpen] = useState(false);
  const [parentForNewCategory, setParentForNewCategory] = useState<string | undefined>();
  const [activeChildTab, setActiveChildTab] = useState('all');
  const [movingCategory, setMovingCategory] = useState<string | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<string | null>(null);
  const parents = novel.entitiesMeta?.codexCategoryParents || novel.codexCategoryParents || {};
  const displayCategoryName = (id: string) => novel.codexCategories[id] || id;
  const childrenOf = (id: string) => Object.keys(novel.codexCategories)
    .filter((child) => parents[child] === id)
    .sort((a, b) => displayCategoryName(a).localeCompare(displayCategoryName(b), undefined, { sensitivity: 'base' }));
  const topLevelCategories = useMemo(() => Object.keys(novel.codexCategories)
    .filter((id) => !parents[id])
    .sort((a, b) => displayCategoryName(a).localeCompare(displayCategoryName(b), undefined, { sensitivity: 'base' })), [novel.codexCategories, parents]);

  const handleAllTabNewEntryClick = () => {
    const categoryKeys = Object.keys(novel.codexCategories);
    if (categoryKeys.length === 0) {
      toast({ title: t('common.error'), description: t('entity_list.codex.tabs.no_categories') });
      return;
    }
    setAllTabNewEntryDialogOpen(true);
  };

  const handleConfirmAllTabNewEntry = (selectedCat: string) => {
    navigate({ to: '/codex/new', search: { type: selectedCat } });
  };

  const categories = useMemo(() => {
    const categoryMap = novel.codexCategories;
    return ['all', ...topLevelCategories];
  }, [topLevelCategories]);

  const filteredEntries = useMemo(() => {
    let result = entries || [];
    if (activeTab !== 'all') {
      const allowed = activeChildTab === 'all' ? [activeTab, ...childrenOf(activeTab)] : [activeChildTab];
      result = result.filter(e => allowed.includes(e.type));
    }
    return sortItems(result);
  }, [entries, activeTab, activeChildTab, parents, sortItems]);

  const handleAddCategory = async (name: string, parentId?: string) => {
    if (!novel.id) return;
    try {
      const categoryId = `category-${Date.now()}`;
      const newCategories = { ...(novel.entitiesMeta?.codexCategories || {}), [categoryId]: name };
      const newParents = { ...(novel.entitiesMeta?.codexCategoryParents || {}) };
      if (parentId || parentForNewCategory) newParents[categoryId] = parentId || parentForNewCategory!;
      const newSettings = { ...(novel.entitiesMeta?.settings || {}) };
      newSettings[`codex:${categoryId}`] = [];

      await updateNovelItem({
        entitiesMeta: {
          ...novel.entitiesMeta,
          codexCategories: newCategories,
          codexCategoryParents: newParents,
          settings: newSettings
        }
      });

      toast({ title: t('common.success'), description: t('entity_list.codex.tabs.category_created') });
      setActiveTab(parentId || parentForNewCategory || categoryId);
      setActiveChildTab(parentId || parentForNewCategory ? categoryId : 'all');
      setParentForNewCategory(undefined);
    } catch (error) {
      toast({ title: t('common.error'), description: t('entity_form.common.save_failed_title'), variant: 'destructive' });
      throw error;
    }
  };

  const handleEditCategory = async (newName: string, parentId?: string) => {
    const categoryId = editingCategory?.oldName;
    if (!novel.id || !categoryId) return;
    if (DEFAULT_CATEGORIES.includes(categoryId)) {
      toast({ title: t('common.error'), description: t('entity_list.codex.tabs.default_cannot_rename'), variant: 'destructive' });
      return;
    }

    try {
      const newCategories = { ...(novel.entitiesMeta?.codexCategories || {}), [categoryId]: newName };
      const newParents = { ...(novel.entitiesMeta?.codexCategoryParents || {}) };
      if (parentId && parentId !== categoryId) newParents[categoryId] = parentId;
      else delete newParents[categoryId];

      await updateNovelItem({
        entitiesMeta: {
          ...novel.entitiesMeta,
          codexCategories: newCategories
          , codexCategoryParents: newParents
        }
      });

      toast({ title: t('common.success'), description: t('entity_list.codex.tabs.category_renamed') });
    } catch (error) {
      toast({ title: t('common.error'), description: t('entity_form.common.save_failed_title'), variant: 'destructive' });
      throw error;
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!novel.id) return;
    if (DEFAULT_CATEGORIES.includes(categoryId)) {
      toast({ title: t('common.error'), description: t('entity_list.codex.tabs.default_cannot_delete'), variant: 'destructive' });
      return;
    }
    const categoryName = novel.codexCategories[categoryId] || categoryId;
    if ((entries || []).some((entry) => entry.type === categoryId)) {
      toast({ title: t('common.error'), description: t('entity_list.codex.tabs.move_entries_before_delete'), variant: 'destructive' });
      return;
    }
    try {
      const newCategories = { ...(novel.entitiesMeta?.codexCategories || {}) };
      delete newCategories[categoryId];
      const newParents = { ...(novel.entitiesMeta?.codexCategoryParents || {}) };
      delete newParents[categoryId];
      Object.keys(newParents).forEach((child) => { if (newParents[child] === categoryId) delete newParents[child]; });

      const key = `codex:${categoryId}`;
      const newSettings = { ...(novel.entitiesMeta?.settings || {}) };
      delete newSettings[key];

      await updateNovelItem({
        entitiesMeta: {
          ...novel.entitiesMeta,
          codexCategories: newCategories,
          codexCategoryParents: newParents,
          settings: newSettings
        }
      });

      toast({ title: t('common.success'), description: t('entity_list.codex.tabs.category_deleted') });
      setActiveTab('all');
    } catch (error) {
      toast({ title: t('common.error'), description: t('entity_form.common.save_failed_title'), variant: 'destructive' });
    }
  };

  const requestDeleteCategory = (categoryId: string) => setDeletingCategory(categoryId);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, cat: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedCategory(cat);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCategory(null);
  };

  const handleMoveCategory = () => {
    if (selectedCategory && childrenOf(selectedCategory).length === 0) setMovingCategory(selectedCategory);
  };

  const saveMoveCategory = async (parentId?: string) => {
    if (!movingCategory) return;
    const next = { ...(novel.entitiesMeta?.codexCategoryParents || {}) };
    if (parentId) next[movingCategory] = parentId; else delete next[movingCategory];
    await updateNovelItem({ entitiesMeta: { ...novel.entitiesMeta, codexCategoryParents: next } });
    setActiveTab(parentId || 'all');
    setActiveChildTab(parentId ? movingCategory : 'all');
  };

  return (
    <AuthGuard>
      <Page
        title={t('entity_list.codex.title')}
        description={t('entity_list.codex.description')}
        layoutMode="scrolling"
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EntityListSortMenu value={sort} onChange={setSort} />
            <Tooltip title={categories.length > 1 ? t('entity_list.codex.settings') : t('entity_list.codex.tabs.no_categories')}>
              <span>
                <IconButton
                  onClick={() => navigate({ to: '/codex/settings' })}
                  disabled={categories.length <= 1}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
                    borderRadius: 2,
                  }}
                >
                  <SettingsIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              onClick={() => { setParentForNewCategory(undefined); setCategoryDialogOpen(true); }}
              disabled={!novel.id}
              startIcon={<PlusCircle sx={{ fontSize: 18 }} />}
              sx={{ borderRadius: 2 }}
            >
              {t('entity_list.codex.tabs.new_category')}
            </Button>
          </Box>
        }
      >

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, display: 'flex', alignItems: 'center' }}>
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              flexGrow: 1,
              '& .MuiTab-root': {
                textTransform: 'none',
                minWidth: 100,
                fontWeight: 500,
                minHeight: 48,
              }
            }}
          >
            {categories.map(categoryId => (
              <Tab
                key={categoryId}
                value={categoryId}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {categoryId === 'all' ? t('entity_list.codex.tabs.all') : t(`entity_list.codex.categories.${categoryId}`, { defaultValue: novel.codexCategories[categoryId] || categoryId })}
                    {categoryId !== 'all' && (
                      <IconButton
                        component="span"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleMenuOpen(e, categoryId);
                        }}
                        sx={{ p: 0.5, opacity: activeTab === categoryId ? 1 : 0, transition: 'opacity 0.2s' }}
                      >
                        <MoreIcon fontSize="inherit" />
                      </IconButton>
                    )}
                  </Box>
                }
                sx={{
                  '&:hover .MuiIconButton-root': { opacity: 1 }
                }}
              />
            ))}
          </Tabs>
        </Box>
        {activeTab !== 'all' && childrenOf(activeTab).length > 0 && (
          <Tabs value={activeChildTab} onChange={(_, value) => setActiveChildTab(value)} variant="scrollable" sx={{ mt: -2, mb: 2, minHeight: 36 }}>
            <Tab value="all" label={t('entity_list.codex.tabs.all')} sx={{ minHeight: 36, py: 0 }} />
            {childrenOf(activeTab).map((child) => (
              <Tab
                key={child}
                value={child}
                label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {novel.codexCategories[child] || child}
                  <IconButton
                    component="span"
                    size="small"
                    onClick={(event) => { event.stopPropagation(); event.preventDefault(); handleMenuOpen(event, child); }}
                    sx={{ p: 0.25, opacity: activeChildTab === child ? 1 : 0, '&:hover': { opacity: 1 } }}
                  >
                    <MoreIcon fontSize="inherit" />
                  </IconButton>
                </Box>}
                sx={{ minHeight: 36, py: 0, '&:hover .MuiIconButton-root': { opacity: 1 } }}
              />
            ))}
          </Tabs>
        )}

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {!selectedCategory || !parents[selectedCategory] ? <MenuItem onClick={() => {
            if (selectedCategory) {
              setParentForNewCategory(selectedCategory);
              setCategoryDialogOpen(true);
              handleMenuClose();
            }
          }}>
            <PlusCircle sx={{ fontSize: 18, mr: 1 }} /> {t('entity_list.codex.tabs.add_subcategory')}
          </MenuItem> : null}
          <MenuItem onClick={() => { handleMoveCategory(); handleMenuClose(); }}>{t('entity_list.codex.tabs.move_category')}</MenuItem>
          <MenuItem onClick={() => {
            if (selectedCategory) {
              setEditingCategory({ oldName: selectedCategory, newName: novel.codexCategories[selectedCategory] || selectedCategory, parentId: parents[selectedCategory] });
              handleMenuClose();
            }
          }}>
            <EditIcon sx={{ fontSize: 18, mr: 1 }} /> {t('entity_list.codex.tabs.rename')}
          </MenuItem>
          <MenuItem onClick={() => {
            if (selectedCategory) {
              requestDeleteCategory(selectedCategory);
              handleMenuClose();
            }
          }} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ fontSize: 18, mr: 1 }} /> {t('entity_list.codex.tabs.delete')}
          </MenuItem>
        </Menu>

        {isLoading && !novel.id && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {!novel.id && !isLoading && (
          <Card sx={{ maxWidth: 500, mx: 'auto', mt: 4, textAlign: 'center', p: 4, borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h2" gutterBottom>{t('entity_list.no_novel_title')}</Typography>
              <Typography variant="body1" color="text.secondary">
                {t('entity_list.no_novel_desc', { entity: t('sidebar.codex').toLowerCase() })}
              </Typography>
            </CardContent>
          </Card>
        )}

        <CodexGrid
          entries={filteredEntries}
          activeTab={activeTab}
          novel={novel}
          onAllTabNewEntryClick={handleAllTabNewEntryClick}
        />

        <NewCategoryDialog
          open={categoryDialogOpen}
          onClose={() => setCategoryDialogOpen(false)}
          onAdd={handleAddCategory}
          parentOptions={topLevelCategories.map((id) => ({ id, name: novel.codexCategories[id] || id }))}
          initialParentId={parentForNewCategory}
        />

        <RenameCategoryDialog
          open={Boolean(editingCategory)}
          onClose={() => setEditingCategory(null)}
          initialName={editingCategory?.newName || ''}
          onSave={handleEditCategory}
          parentOptions={topLevelCategories.filter((id) => id !== editingCategory?.oldName && childrenOf(editingCategory?.oldName || '').length === 0).map((id) => ({ id, name: novel.codexCategories[id] || id }))}
          initialParentId={editingCategory?.parentId}
        />
        <MoveCategoryDialog
          open={Boolean(movingCategory)}
          categoryName={movingCategory ? novel.codexCategories[movingCategory] || movingCategory : ''}
          initialParent={movingCategory ? parents[movingCategory] : undefined}
          options={topLevelCategories.filter((id) => id !== movingCategory).map((id) => ({ id, name: novel.codexCategories[id] || id }))}
          onClose={() => setMovingCategory(null)}
          onSave={saveMoveCategory}
        />
        <Dialog open={Boolean(deletingCategory)} onClose={() => setDeletingCategory(null)} maxWidth="xs" fullWidth>
          <DialogTitle>{t('entity_list.codex.tabs.delete_category_title')}</DialogTitle>
          <DialogContent>
            {t('entity_list.codex.tabs.confirm_delete_category', { category: deletingCategory ? novel.codexCategories[deletingCategory] || deletingCategory : '' })}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeletingCategory(null)}>{t('common.cancel')}</Button>
            <Button color="error" variant="contained" onClick={() => { if (deletingCategory) void handleDeleteCategory(deletingCategory); setDeletingCategory(null); }}>{t('entity_list.codex.tabs.delete')}</Button>
          </DialogActions>
        </Dialog>

        <CategorySelectionDialog
          open={allTabNewEntryDialogOpen}
          onClose={() => setAllTabNewEntryDialogOpen(false)}
          categories={novel.codexCategories}
          parents={novel.entitiesMeta?.codexCategoryParents || novel.codexCategoryParents}
          onConfirm={handleConfirmAllTabNewEntry}
        />
      </Page>
    </AuthGuard>
  );
}

function CodexPage() {
  return (
    <FeatureGuard featureKey="should_display_codex_page">
      <Suspense fallback={
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      }>
        <CodexContent />
      </Suspense>
    </FeatureGuard>
  );
}
